import React from 'react'
import { createHashRouter, Navigate, useParams } from 'react-router-dom'

import lazy from '@helpers/lazy'
import routes from '@msteams/Routes/routes'
import ContactListLayout from '@pages/Accounting/Contacts/components/ContactListLayout'
import { ProtectedRoute } from '@src/Routes'
import TeamHome from '@src/pages/Team/components/TeamHome'
import { Perm } from '@type/permissions'
import type { PermCodename } from '@type/permissions'
import { FeatureKey } from '@type/featureKeys'
import ContactLayout from '@pages/Accounting/Contacts/components/ContactLayout'
import AppLayout from '@msteams/components/AppLayout'
import TeamsLanding from '@msteams/components/Navigation/TeamsLanding'
import OnboardingLayout from '@msteams/components/OnboardingLayout'
import TeamLayout from '@pages/Team/components/Layout'

import CostCenterList from '@src/pages/Accounting/CostCenter/List'
import CostCenterForm from '@src/pages/Accounting/CostCenter/Form'
import CostCenterDetails from '@src/pages/Accounting/CostCenter/Details'
import CostCenterDetailsBudget from '@src/pages/Accounting/CostCenter/Details/components/BudgetTab'
import Paywall from '@pages/Paywall'

const ContractsList = lazy(() => import('@src/pages/Accounting/Contracts/List'), 'ContractsList')
const ContractForm = lazy(() => import('@src/pages/Accounting/Contacts/Contracts/ContractForm'), 'ContractForm')
const QuotesList = lazy(() => import('@src/pages/Accounting/Quotes/List'), 'QuotesList')
const QuoteDetails = lazy(() => import('@src/pages/Accounting/Quotes/Details'), 'QuoteDetails')
const VendorsList = lazy(() => import('@src/pages/Accounting/Contacts/ContactsList'), 'VendorsList')
const VendorForm = lazy(() => import('@src/pages/Accounting/Contacts/ContactForm'), 'VendorForm')
const InvoicesList = lazy(() => import('@src/pages/Accounting/Invoices/List'), 'InvoicesList')
const InvoiceDetails = lazy(() => import('@src/pages/Accounting/Invoices/Details'), 'InvoiceDetails')
const PurchaseOrdersList = lazy(() => import('@src/pages/Accounting/PurchaseOrders/List'), 'PurchaseOrdersList')
const PurchaseOrderDetails = lazy(() => import('@src/pages/Accounting/PurchaseOrders/Details'), 'PurchaseOrderDetails')
const VendorContractsList = lazy(
  () => import('@src/pages/Accounting/Contacts/Contracts/ContractsList'),
  'VendorContractsList',
)

const ApprovalRequests = lazy(() => import('@msteams/pages/ApprovalRequests'), 'ApprovalRequests')
const ApprovalRequestDetails = lazy(() => import('@msteams/pages/ApprovalRequestDetails'), 'ApprovalRequestDetails')
const ActionsPage = lazy(() => import('@src/pages/Actions'), 'ActionsPage')
const ActionDetailPage = lazy(() => import('@src/pages/Actions/ActionDetail'), 'ActionDetailPage')

const ApprovalRequestsForm = lazy(
  () => import('@src/pages/ApprovalRequests/RequestForm/CustomForm'),
  'ApprovalRequestsForm',
)

const PipelineBuilder = lazy(() => import('@pages/Pipelines/Builder'), 'PipelineBuilder')
const PipelinesList = lazy(() => import('@pages/Pipelines/PipelinesList'), 'PipelinesList')
const ProcessDetails = lazy(() => import('@pages/Pipelines/ProcessDetails'), 'ProcessDetails')
const ProcessesList = lazy(() => import('@pages/Pipelines/ProcessesList'), 'ProcessesList')

const TeamDashboard = lazy(() => import('@msteams/pages/TeamDashboard'), 'TeamDashboard')
const Config = lazy(() => import('@msteams/pages/Config'), 'Config')
const NotSynchronized = lazy(() => import('@msteams/pages/NotSynchronized'), 'NotSynchronized')

const RFQsList = lazy(() => import('@src/pages/Accounting/RFQ/List'), 'RfqList')

/** MS Teams contact list / form route gate — mirrors the web app: the nav
 * exposes the list through the ``vendors`` / ``customers`` feature, so
 * the route can't gate on ACCOUNTING_MANAGE without redirecting
 * view-only users. Resolve the matching feature from the
 * ``contactParty`` URL param; the FORM composes the accounting write perm
 * on top.
 */
export const MsTeamsContactsListGate: React.FC<React.PropsWithChildren<{ requirePerm?: PermCodename }>> = ({
  children,
  requirePerm,
}) => {
  const { contactParty } = useParams<{ contactParty?: string }>()
  // Only 'customer' / 'vendor' (or legacy undefined → vendor) are valid; a
  // malformed party must not silently authorize as a vendor route.
  if (contactParty && contactParty !== 'customer' && contactParty !== 'vendor') {
    return <Navigate to={routes.public.notFound} replace />
  }
  const feature = contactParty === 'customer' ? FeatureKey.customers : FeatureKey.vendors
  return (
    <ProtectedRoute feature={feature} requirePerm={requirePerm}>
      {children}
    </ProtectedRoute>
  )
}
const RFQDetails = lazy(() => import('@src/pages/Accounting/RFQ/Details'), 'RfqDetails')
const RFQProposalDetails = lazy(() => import('@src/pages/Accounting/RFQ/ProposalDetails'), 'RfqProposalDetails')

const Auth = lazy(() => import('@msteams/pages/Auth'), 'Auth')
const AuthLayout = lazy(() => import('@msteams/components/AuthLayout'), 'AuthLayout')

const Tasks = lazy(() => import('@pages/Tasks/List'), 'Tasks')

const NotFound = lazy(() => import('@pages/NotFound'), 'NotFound')
const Onboarding = lazy(() => import('@pages/Onboarding'), 'Onboarding')
const Assistant = lazy(() => import('@pages/Assistant'), 'Assistant')

const SettingsPage = lazy(() => import('@src/pages/Settings'), 'SettingsPage')
const TeamSettingsPage = lazy(() => import('@src/pages/TeamSettings'), 'TeamSettingsPage')

const ReportsList = lazy(() => import('@pages/Reports/List'), 'ReportsList')
const ManualReports = lazy(() => import('@pages/Reports/List/ManualReports'), 'ManualReports')
const ReportForm = lazy(() => import('@pages/Reports/ReportForm'), 'ReportForm')

const Mailbox = lazy(() => import('@pages/Mailbox'), 'Mailbox')

const OrganizationalStructureList = lazy(() => import('@src/pages/Team/Members/List'), 'OrganizationalStructureList')
const OrganizationalStructureMember = lazy(
  () => import('@src/pages/Team/Members/Member'),
  'OrganizationalStructureMember',
)
const TeamDepartments = lazy(() => import('@src/pages/Team/Departments'), 'TeamDepartments')
const TeamLocations = lazy(() => import('@src/pages/Team/Locations'), 'TeamLocations')
const CustomLists = lazy(() => import('@src/pages/Pipelines/CustomLists'), 'CustomLists')
const TeamRoles = lazy(() => import('@src/pages/Team/Roles'), 'TeamRoles')
const OrganisationsList = lazy(
  () => import('@src/pages/Accounting/Organizations/OrganizationsList'),
  'OrganisationsList',
)
const OrganizationForm = lazy(() => import('@src/pages/Accounting/Organizations/OrganizationForm'), 'OrganizationForm')

const FinanceSettingsLayout = lazy(
  () => import('@src/pages/Accounting/FinanceSettings/components/Layout'),
  'FinanceSettingsLayout',
)
const AccountsList = lazy(() => import('@src/pages/Accounting/Accounts/AccountsList'), 'AccountsList')
const CategoriesList = lazy(() => import('@src/pages/Accounting/Accounts/CategoriesList'), 'CategoriesList')
const CompanyDetails = lazy(() => import('@src/pages/Accounting/FinanceSettings/CompanyDetails'), 'CompanyDetails')
const TaxList = lazy(() => import('@src/pages/Accounting/Taxes/TaxList'), 'TaxList')
const ProductsList = lazy(() => import('@src/pages/Accounting/Products/List'), 'ProductsList')
const EntitiesList = lazy(() => import('@src/pages/Accounting/FinanceSettings/Entities'), 'EntitiesList')

const commonRoutes = [
  {
    path: routes.public.auth,
    element: <Auth />,
  },
  {
    path: routes.public.notFound,
    element: <NotFound />,
  },
]

// Mirror the web app's PipelineByIdGate: ``/workflow/new`` is a create
// (WORKFLOW_ADD); an existing id only needs WORKFLOW_VIEW because the
// builder renders read-only without WORKFLOW_CHANGE. Both are narrower
// than the `workflows` feature (which also admits custom_list.view).
export const MsTeamsPipelineByIdGate: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const isCreate = id === 'new'
  return (
    <ProtectedRoute feature={FeatureKey.workflows} requirePerm={isCreate ? Perm.WORKFLOW_ADD : Perm.WORKFLOW_VIEW}>
      <PipelineBuilder />
    </ProtectedRoute>
  )
}

export const personalRouter = createHashRouter([
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      {
        element: <OnboardingLayout />,
        children: [
          {
            element: <AppLayout />,
            children: [
              // Landing: the first Teams nav entry this viewer may use,
              // from the same resolved feature list the nav reads.
              {
                index: true,
                element: <TeamsLanding scope="personal" />,
              },
              {
                path: routes.private.tab,
                element: <TeamsLanding scope="personal" />,
              },
              {
                path: routes.private.dashboard,
                element: <TeamsLanding scope="personal" />,
              },
              // Gated like the web router. The request DETAIL routes below
              // stay ungated on purpose: a named approver reaches a request
              // through the approver relationship, not request.view, and the
              // backend answers 403/404 for a non-party.
              {
                path: routes.private.approvalRequests,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests}>
                    <ApprovalRequests />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.approvalRequestsById,
                element: <ApprovalRequestDetails />,
              },
              {
                path: routes.private.approvalRequestsByIdAction,
                element: <ApprovalRequestDetails />,
              },
              // The MCP actions banner renders inside the shared request
              // detail page, and its Review link targets these. Without
              // them the Teams build sends the user to a bare 404.
              {
                path: routes.private.actions,
                element: (
                  <ProtectedRoute feature={FeatureKey.actions}>
                    <ActionsPage />
                  </ProtectedRoute>
                ),
              },
              {
                // No feature / requirePerm, matching web. A designated
                // executor asked to confirm an action need not hold
                // request.view, and the backend's visible_execution_queryset
                // is the row filter that authorizes this page. See
                // src/Routes/index.tsx.
                path: routes.private.actionById,
                element: (
                  <ProtectedRoute onlyWithMcpActions>
                    <ActionDetailPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.approvalRequest,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests} requirePerm={Perm.REQUEST_ADD}>
                    <ApprovalRequestsForm />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.approvalRequestEditById,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests}>
                    <ApprovalRequestsForm />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.notSynchronized,
                element: <NotSynchronized />,
              },
              {
                path: routes.private.tasks,
                element: (
                  <ProtectedRoute feature={FeatureKey.tasks}>
                    <Tasks />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.taskBoards,
                element: (
                  <ProtectedRoute feature={FeatureKey.tasks}>
                    <Tasks />
                  </ProtectedRoute>
                ),
              },
              // AI Assistant — mirrors the web router pattern at
              // src/Routes/index.tsx (parent route + ':threadId' child
              // sub-route so /assistant/:threadId resolves to the same
              // <Assistant /> component, with React Router driving the
              // thread switch via URL params).
              {
                path: routes.private.assistant,
                element: <Assistant />,
                children: [{ path: ':threadId', element: null }],
              },

              {
                path: routes.private.purchaseOrders,
                element: (
                  <ProtectedRoute feature={FeatureKey.purchaseOrders}>
                    <PurchaseOrdersList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.purchaseOrdersById,
                element: (
                  <ProtectedRoute feature={FeatureKey.purchaseOrders}>
                    <PurchaseOrderDetails />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.bills,
                element: (
                  <ProtectedRoute feature={FeatureKey.bills}>
                    <InvoicesList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.billById,
                element: (
                  <ProtectedRoute feature={FeatureKey.bills}>
                    <InvoiceDetails />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.invoices,
                element: (
                  <ProtectedRoute feature={FeatureKey.invoices}>
                    <InvoicesList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.invoiceById,
                element: (
                  <ProtectedRoute feature={FeatureKey.invoices}>
                    <InvoiceDetails />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.quotes,
                element: (
                  <ProtectedRoute feature={FeatureKey.quotes}>
                    <QuotesList />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.quoteById,
                element: (
                  <ProtectedRoute feature={FeatureKey.quotes}>
                    <QuoteDetails />
                  </ProtectedRoute>
                ),
              },

              {
                element: <ContactListLayout />,
                children: [
                  {
                    path: routes.private.contacts,
                    element: (
                      <MsTeamsContactsListGate>
                        <VendorsList />
                      </MsTeamsContactsListGate>
                    ),
                  },
                  {
                    path: routes.private.contactsList,
                    element: (
                      <MsTeamsContactsListGate>
                        <VendorsList />
                      </MsTeamsContactsListGate>
                    ),
                  },
                ],
              },
              {
                element: <ContactLayout />,
                children: [
                  {
                    path: routes.private.contact,
                    element: (
                      <MsTeamsContactsListGate requirePerm={Perm.ACCOUNTING_MANAGE}>
                        <VendorForm />
                      </MsTeamsContactsListGate>
                    ),
                  },
                  {
                    path: routes.private.contactContracts,
                    element: (
                      <ProtectedRoute feature={FeatureKey.contracts}>
                        <VendorContractsList />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: routes.private.contactContract,
                element: (
                  <ProtectedRoute feature={FeatureKey.contracts}>
                    <ContractForm />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.processes,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests}>
                    <ProcessesList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.pipelineProcesses,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests}>
                    <ProcessesList />
                  </ProtectedRoute>
                ),
              },
              {
                // Mirror the web route's gate. The `workflows` feature is a
                // container (it also admits custom_list.view); each tab
                // keeps its own perm on top.
                path: routes.private.pipelines,
                element: (
                  <ProtectedRoute feature={FeatureKey.workflows} requirePerm={Perm.WORKFLOW_VIEW}>
                    <PipelinesList />
                  </ProtectedRoute>
                ),
              },
              // Mirrors the web router: a tab of the Workflows section, since
              // custom lists exist to feed workflow form fields.
              {
                path: routes.private.customLists,
                element: (
                  <ProtectedRoute feature={FeatureKey.workflows} requirePerm={Perm.CUSTOM_LIST_VIEW}>
                    <CustomLists />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.customListById,
                element: (
                  <ProtectedRoute feature={FeatureKey.workflows} requirePerm={Perm.CUSTOM_LIST_VIEW}>
                    <CustomLists />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.pipelineProcessesById,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests}>
                    <ProcessDetails />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.pipelineProcesses,
                element: <Navigate to={routes.private.approvalRequests} />,
              },
              {
                path: routes.private.pipelineApprovalRequest,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests} requirePerm={Perm.REQUEST_ADD}>
                    <ApprovalRequestsForm />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.pipelineProcessApprovalRequest,
                element: (
                  <ProtectedRoute feature={FeatureKey.approvalRequests} requirePerm={Perm.REQUEST_ADD}>
                    <ApprovalRequestsForm />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.pipelineById,
                element: <MsTeamsPipelineByIdGate />,
              },
              {
                path: routes.private.rfqs,
                element: (
                  <ProtectedRoute feature={FeatureKey.rfqs}>
                    <RFQsList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.rfqProposalsById,
                element: (
                  <ProtectedRoute feature={FeatureKey.rfqs}>
                    <RFQProposalDetails />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.rfqById,
                element: (
                  <ProtectedRoute feature={FeatureKey.rfqs}>
                    <RFQDetails />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.settings,
                element: <SettingsPage />,
                children: [
                  {
                    index: true,
                    path: routes.private.settingsRouted,
                    element: <SettingsPage />,
                  },
                ],
              },
              // Team (Workspace) Settings — mirror of the web router so the
              // Teams embed reaches the same tabs (Team, Tags, Integrations,
              // Notifications, Audit Logs, API Dashboard). Same shared page +
              // `team_settings` feature gate; the page filters tabs per-perm.
              {
                path: routes.private.teamSettings,
                element: (
                  <ProtectedRoute feature={FeatureKey.teamSettings}>
                    <TeamSettingsPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.teamSettingsRouted,
                element: (
                  <ProtectedRoute feature={FeatureKey.teamSettings}>
                    <TeamSettingsPage />
                  </ProtectedRoute>
                ),
              },

              {
                element: <ReportsList />,
                children: [
                  {
                    path: routes.private.reports,
                    element: (
                      <ProtectedRoute feature={FeatureKey.reports}>
                        <ManualReports />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: routes.private.createReport,
                element: (
                  <ProtectedRoute feature={FeatureKey.reports}>
                    <ReportForm />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.reportById,
                element: (
                  <ProtectedRoute feature={FeatureKey.reports}>
                    <ReportForm />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.mailbox,
                element: (
                  <ProtectedRoute feature={FeatureKey.mailbox}>
                    <Mailbox />
                  </ProtectedRoute>
                ),
              },
              // The `team` feature is a container (Members / Departments /
              // Locations / Roles, each on its own codename): the section
              // entry admits any of them, each tab keeps its own perm on top.
              {
                element: <TeamLayout />,
                children: [
                  {
                    path: routes.private.organizationalStructure,
                    element: (
                      <ProtectedRoute feature={FeatureKey.team}>
                        <TeamHome roster={<OrganizationalStructureList />} />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamDepartments,
                    element: (
                      <ProtectedRoute feature={FeatureKey.team} requirePerm={Perm.TEAM_VIEW}>
                        <TeamDepartments />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamDepartmentById,
                    element: (
                      <ProtectedRoute feature={FeatureKey.team} requirePerm={Perm.TEAM_VIEW}>
                        <TeamDepartments />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamLocations,
                    element: (
                      <ProtectedRoute feature={FeatureKey.team} requirePerm={Perm.TEAM_VIEW}>
                        <TeamLocations />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamLocationById,
                    element: (
                      <ProtectedRoute feature={FeatureKey.team} requirePerm={Perm.TEAM_VIEW}>
                        <TeamLocations />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.teamRoles,
                    element: (
                      <ProtectedRoute feature={FeatureKey.team} requirePerm={Perm.ROLE_VIEW}>
                        <TeamRoles />
                      </ProtectedRoute>
                    ),
                  },
                ],
              },
              {
                path: routes.private.organizationalStructureById,
                element: (
                  <ProtectedRoute feature={FeatureKey.team} requirePerm={Perm.TEAM_VIEW}>
                    <OrganizationalStructureMember />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.contracts,
                element: (
                  <ProtectedRoute feature={FeatureKey.contracts}>
                    <ContractsList />
                  </ProtectedRoute>
                ),
              },
              // Orgs share Company details' gate (accounting.manage_settings).
              {
                path: routes.private.organizations,
                element: (
                  <ProtectedRoute onlyWithOrgsAvailable feature={FeatureKey.companyDetails}>
                    <OrganisationsList />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.organization,
                element: (
                  <ProtectedRoute onlyWithOrgsAvailable feature={FeatureKey.companyDetails}>
                    <OrganizationForm />
                  </ProtectedRoute>
                ),
              },
              {
                element: <FinanceSettingsLayout />,
                children: [
                  {
                    path: routes.private.accounts,
                    element: (
                      <ProtectedRoute feature={FeatureKey.accounts}>
                        <AccountsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.accountById,
                    element: (
                      <ProtectedRoute feature={FeatureKey.accounts}>
                        <AccountsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.taxes,
                    element: (
                      <ProtectedRoute feature={FeatureKey.taxes}>
                        <TaxList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.taxById,
                    element: (
                      <ProtectedRoute feature={FeatureKey.taxes}>
                        <TaxList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.products,
                    element: (
                      <ProtectedRoute feature={FeatureKey.products}>
                        <ProductsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.productById,
                    element: (
                      <ProtectedRoute feature={FeatureKey.products}>
                        <ProductsList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.accountCategories,
                    element: (
                      <ProtectedRoute feature={FeatureKey.categories}>
                        <CategoriesList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.accountCategoryById,
                    element: (
                      <ProtectedRoute feature={FeatureKey.categories}>
                        <CategoriesList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    // Entities tab (SoftLedger Locations) — the shared
                    // FinanceSettings header shows it in Teams too, but the
                    // route was only ever registered in the web router
                    // (ADN-436 parity miss). Same gate as web.
                    path: routes.private.entities,
                    element: (
                      <ProtectedRoute feature={FeatureKey.companyDetails}>
                        <EntitiesList />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.companyDetails,
                    element: (
                      <ProtectedRoute feature={FeatureKey.companyDetails}>
                        <CompanyDetails />
                      </ProtectedRoute>
                    ),
                  },
                  {
                    path: routes.private.financeSettings,
                    element: <Navigate to={routes.private.accounts} />,
                  },
                ],
              },
              {
                path: routes.private.costCenter,
                element: (
                  <ProtectedRoute feature={FeatureKey.costCenters}>
                    <CostCenterList />
                  </ProtectedRoute>
                ),
              },

              {
                path: routes.private.costCenterById,
                element: (
                  <ProtectedRoute feature={FeatureKey.costCenters}>
                    <CostCenterDetails />
                  </ProtectedRoute>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="budget" replace />,
                  },

                  {
                    path: routes.private.costCenterBudget,
                    element: <CostCenterDetailsBudget />,
                  },
                ],
              },
              {
                // Create / edit need accounting.manage on top of the feature.
                path: routes.private.costCenterEditById,
                element: (
                  <ProtectedRoute feature={FeatureKey.costCenters} requirePerm={Perm.ACCOUNTING_MANAGE}>
                    <CostCenterForm />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.paywall,
                element: <Paywall />,
              },
            ],
          },
        ],
      },
      {
        path: routes.private.onboarding,
        element: <Onboarding />,
      },
    ],
  },

  ...commonRoutes,
])

export const teamRouter = createHashRouter([
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      {
        element: <OnboardingLayout />,
        children: [
          {
            element: <AppLayout />,
            children: [
              // Same landing rule as personalRouter, restricted to the pages
              // THIS router registers (the team overview at the
              // approval-requests path, and the assistant).
              {
                index: true,
                element: <TeamsLanding scope="team" />,
              },
              {
                path: routes.private.tab,
                element: <TeamsLanding scope="team" />,
              },
              {
                path: routes.private.dashboard,
                element: <TeamsLanding scope="team" />,
              },
              {
                // Deliberately NOT behind ProtectedRoute. A denial there
                // redirects to the web landing route, and this router
                // registers almost none of those pages — the user would land
                // on nothing. The overview handles its own 403 (it sends the
                // user to /not-synchronized, which this router does register).
                path: routes.private.approvalRequests,
                element: <TeamDashboard />,
              },
              {
                path: routes.private.assistant,
                element: <Assistant />,
                children: [{ path: ':threadId', element: null }],
              },
              // Mirrored from personalRouter: the request detail page is
              // reachable in the team tab too, and its MCP banner links here.
              {
                path: routes.private.actions,
                element: (
                  <ProtectedRoute feature={FeatureKey.actions}>
                    <ActionsPage />
                  </ProtectedRoute>
                ),
              },
              {
                // No feature / requirePerm, matching web. A designated executor asked
                // to confirm an action need not hold request.view, and the
                // backend's visible_execution_queryset is the row filter
                // that authorizes this page. See src/Routes/index.tsx.
                path: routes.private.actionById,
                element: (
                  <ProtectedRoute onlyWithMcpActions>
                    <ActionDetailPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: routes.private.notSynchronized,
                element: <NotSynchronized />,
              },
            ],
          },
        ],
      },
      {
        path: routes.private.onboarding,
        element: <Onboarding />,
      },
    ],
  },
  {
    path: routes.private.config,
    element: <Config />,
  },
  ...commonRoutes,
])
